import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import toast from 'react-hot-toast';

import { getReturnOnboardingData } from '@/service/returnChatService';
import type {
  ReturnOnboardingDetails,
} from '@/types';

/**
 * GuestContext — Centralized state for guest onboarding data.
 * Requires an orderId to fetch onboarding details.
 */

interface GuestContextType {
  returnOnboardingData: ReturnOnboardingDetails | null;
  isLoading: boolean;
  error: string | null;
  fetchOnboardingData: (orderId: string, isManual?: boolean) => Promise<ReturnOnboardingDetails | null>;
}

const GuestContext = createContext<GuestContextType | undefined>(undefined);

interface GuestProviderProps {
  children: ReactNode;
}

export const GuestProvider: React.FC<GuestProviderProps> = ({ children }) => {
  const [onboarding, setOnboarding] = useState<ReturnOnboardingDetails | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const onboardingRef = React.useRef<ReturnOnboardingDetails | null>(null);

  const fetchOnboardingData = useCallback(
    async (orderId: string, isManual = false): Promise<ReturnOnboardingDetails | null> => {
      if (!orderId) return null;

      // Optimization: Skip fetching if we already have the data for this order, 
      // unless it's a manual refresh.
      if (!isManual && onboardingRef.current && onboardingRef.current.order_details?.id === orderId) {
        return onboardingRef.current;
      }

      setIsLoading(true);
      setError(null);

      const fetchPromise = getReturnOnboardingData(orderId);

      if (isManual) {
        toast.promise(fetchPromise, {
          loading: 'Refreshing order data...',
          success: 'Data refreshed successfully!',
          error: 'Failed to refresh data. Please try again.',
        });
      }

      try {
        const res = await fetchPromise;

        if (res?.success && res?.data) {
          onboardingRef.current = res.data;
          setOnboarding(res.data);
          return res.data;
        } else {
          throw new Error('Failed to fetch onboarding data');
        }
      } catch (err: unknown) {
        console.error('GuestContext fetch error:', err);

        const message =
          err instanceof Error
            ? err.message
            : 'Unknown error while fetching onboarding data';

        setError(message);

        if (isManual) {
          throw err;
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return (
    <GuestContext.Provider
      value={{
        returnOnboardingData: onboarding,
        isLoading,
        error,
        fetchOnboardingData,
      }}
    >
      {children}
    </GuestContext.Provider>
  );
};

export const useGuestContext = (): GuestContextType => {
  const context = useContext(GuestContext);

  if (!context) {
    throw new Error('useGuestContext must be used within a GuestProvider');
  }

  return context;
};